import { sendCustomError } from "@otto-ec/global-resources/apm";
import { createLogger } from "utils/logging";
import { DetailviewService } from "./DetailviewService";
import { TrackingService } from "./tracking/TrackingService";

const attributeNameBenefitId = "benefit-id";
const attributeNameVariationId = "variation-id";
const attributeNameProductId = "product-id";
export const attributeNameTriggerReload = "trigger-reload"; /*                                                                                           */
const attributeNameEventMergeId = "event-merge-id";
const attributeNameFeature = "feature";
const attributeNameTeam = "team";
const attributeNameIsPrefetched = "is-prefetched";

const log = createLogger("tag", "detailview.component");

export class DetailviewElement extends HTMLElement {
	static observedAttributes = [attributeNameFeature, attributeNameTeam, attributeNameBenefitId, attributeNameVariationId, attributeNameProductId, attributeNameTriggerReload, attributeNameEventMergeId];
	private readonly trackingService: TrackingService;
	private readonly detailviewService: DetailviewService;

	constructor() {
		super(); /*                                     */
		this.isConnected = false;
		this.trackingService = new TrackingService();
		this.detailviewService = new DetailviewService(this.trackingService);
	}

	private _isConnected = false;

	override get isConnected() {
		return this._isConnected;
	}

	override set isConnected(value: boolean) {
		this._isConnected = value;
	}

	async attributeChangedCallback(name: string, oldValue: string, newValue: string) {
		log.info("Attribute update: ", name, oldValue, newValue, this.isConnected);
		if (this.isConnected) {
			this.firstElementChild?.remove();
			await this.callBackend();
		}
	}

	async connectedCallback() {
		this.isConnected = true;
		if (this.getAttribute(attributeNameIsPrefetched) === "true") {
			log.info("Detailview is prefetched, skipping backend call.");
			this.initDetailview();
			return;
		}
		await this.callBackend();
	}

	async callBackend() {
		if (this.setPreRenderedContent()) {
			log.info("Use pre-rendered content for detailview area, skip backend call.");
			return;
		}

		const urlSearchParams = this.getSearchParams();
		log.info("Calling tag for %s with %s", this.id, JSON.stringify(urlSearchParams));

		let tries = 0;
		let success = false;
		const status_code = [];

		if (this.parentElement) {
			this.parentElement.style.display = "none";
		}

		while (tries++ < 3 && !success) {
			let response: Response;
			try {
				response = await fetch(`/benefit-tag/detailview?${urlSearchParams}`, {
					headers: {
						Accept: "text/html;charset=UTF-8",
						"X-Requested-With": "XMLHttpRequest",
					},
					priority: "high",
				});
			} catch (error) {
				log.warn("tag detailview request returns an error!", error);

				if (error instanceof DOMException) {
					status_code.push(error.name);
				} else {
					status_code.push(error);
				}
				continue;
			}

			status_code.push(response.status);
			success = response.ok;

			if (success) {
				/*                          */
				response.text().then((text) => {
					if (this.parentElement && text !== "") {
						this.parentElement.style.display = "block";
					}
					this.innerHTML = text;
					this.initDetailview();
				});
				break;
			}
			log.warn("detailview endpoint returns a non ok response!", response.status);
		}

		if (!success) {
			sendCustomError("ft9-benefit-detailview-not-ok", "callBackend error", {
				urlSearchParams: urlSearchParams.toString(),
				status_code: status_code.join(":"),
				tries: tries - 1,
			});
		}
	}

	private setPreRenderedContent() {
		const productId = this.getAttribute(attributeNameProductId);
		const variationId = this.getAttribute(attributeNameVariationId);
		const autoActivationBenefitId = this.getAttribute(attributeNameBenefitId);

		const wrapperElement = document.getElementById(`benefit-detailview-data-${productId}-${variationId}`);
		if (!wrapperElement) {
			return false;
		}
		/*                                                                            */
		if (wrapperElement.dataset.autoActivationBenefitId != autoActivationBenefitId) {
			return false;
		}
		const firstChildren = wrapperElement.children.item(0);
		if (!firstChildren) {
			return false;
		}

		this.replaceChildren(firstChildren);
		wrapperElement.remove();
		this.initDetailview();
		return true;
	}

	private getSearchParams() {
		const urlSearchParams = new URLSearchParams();

		function addToSearchParam(parmName: string, value: string | null) {
			if (value != null) {
				urlSearchParams.append(parmName, value);
			}
		}

		addToSearchParam("variationId", this.getAttribute(attributeNameVariationId));
		addToSearchParam("benefitId", this.getAttribute(attributeNameBenefitId));
		addToSearchParam("productId", this.getAttribute(attributeNameProductId));
		addToSearchParam("team", this.getAttribute(attributeNameTeam));
		addToSearchParam("feature", this.getAttribute(attributeNameFeature));
		return urlSearchParams;
	}

	private initDetailview() {
		const variationId = this.getAttribute(attributeNameVariationId);
		const eventMergeId = this.getAttribute(attributeNameEventMergeId);

		if (!variationId) {
			return;
		}

		this.detailviewService.initialize(variationId, this, eventMergeId);
	}
}

customElements.define("ft9-detailview-v1", DetailviewElement);
